import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './ImageCropper.css';

// Target dimensions for slider images
const TARGET_WIDTH = 1920;
const TARGET_HEIGHT = 720;
const ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT; // 8:3

/**
 * Creates a canvas-based cropped image from the crop area
 */
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = TARGET_WIDTH;
  canvas.height = TARGET_HEIGHT;

  // Fill with white background first
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);

  // Draw the cropped image scaled to target dimensions
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    TARGET_WIDTH,
    TARGET_HEIGHT
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
};

/**
 * Creates an image centered with white padding to fit target dimensions
 */
const createPaddedImage = async (imageSrc) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = TARGET_WIDTH;
  canvas.height = TARGET_HEIGHT;

  // Fill with white background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);

  // Calculate scaling to fit the image within target dimensions while maintaining aspect ratio
  const imageAspect = image.width / image.height;
  let drawWidth, drawHeight;

  if (imageAspect > ASPECT_RATIO) {
    // Image is wider than target - fit by width
    drawWidth = TARGET_WIDTH;
    drawHeight = TARGET_WIDTH / imageAspect;
  } else {
    // Image is taller than target - fit by height
    drawHeight = TARGET_HEIGHT;
    drawWidth = TARGET_HEIGHT * imageAspect;
  }

  // Center the image
  const x = (TARGET_WIDTH - drawWidth) / 2;
  const y = (TARGET_HEIGHT - drawHeight) / 2;

  ctx.drawImage(image, x, y, drawWidth, drawHeight);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
};

/**
 * Helper function to load an image
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

/**
 * Calculate the cropped area pixels
 */
const getCroppedAreaPixels = (imageDimensions, croppedAreaPercentage) => {
  const { width, height } = imageDimensions;
  return {
    x: Math.round((croppedAreaPercentage.x / 100) * width),
    y: Math.round((croppedAreaPercentage.y / 100) * height),
    width: Math.round((croppedAreaPercentage.width / 100) * width),
    height: Math.round((croppedAreaPercentage.height / 100) * height),
  };
};

/**
 * ImageCropper Component
 * Provides cropping functionality for slider images with 1920x720 resolution
 */
function ImageCropper({ 
  imageSrc, 
  onCropComplete, 
  onCancel, 
  imageNumber,
  originalFileName 
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    if (!croppedAreaPixels) return;
    
    setProcessing(true);
    try {
      const croppedBlob = await createCroppedImage(imageSrc, croppedAreaPixels);
      const fileName = originalFileName.replace(/\.[^/.]+$/, '') + '_cropped.jpg';
      const file = new File([croppedBlob], fileName, { type: 'image/jpeg' });
      onCropComplete(file, imageNumber);
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Error processing image. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleSkipCrop = async () => {
    setProcessing(true);
    try {
      const paddedBlob = await createPaddedImage(imageSrc);
      const fileName = originalFileName.replace(/\.[^/.]+$/, '') + '_padded.jpg';
      const file = new File([paddedBlob], fileName, { type: 'image/jpeg' });
      onCropComplete(file, imageNumber);
    } catch (error) {
      console.error('Error padding image:', error);
      alert('Error processing image. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="image-cropper-overlay">
      <div className="image-cropper-modal">
        <div className="image-cropper-header">
          <h3>Crop Image {imageNumber} for Slider</h3>
          <p className="image-cropper-info">
            Target resolution: <strong>{TARGET_WIDTH} × {TARGET_HEIGHT}</strong> pixels
          </p>
        </div>

        <div className="image-cropper-container">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={ASPECT_RATIO}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        <div className="image-cropper-controls">
          <label className="zoom-label">
            Zoom: {zoom.toFixed(2)}x
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="zoom-slider"
            />
          </label>
        </div>

        <div className="image-cropper-actions">
          <button 
            className="btn btn-secondary" 
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </button>
          <button 
            className="btn btn-outline-primary" 
            onClick={handleSkipCrop}
            disabled={processing}
            title="Center image and pad with white background"
          >
            {processing ? 'Processing...' : 'Skip Crop (Auto-Pad)'}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleCrop}
            disabled={processing}
          >
            {processing ? 'Processing...' : 'Apply Crop'}
          </button>
        </div>

        <p className="image-cropper-hint">
          💡 <strong>Tip:</strong> Use "Skip Crop" to center your image with white padding if it's too small or you don't want to crop.
        </p>
      </div>
    </div>
  );
}

export default ImageCropper;
